import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import Stripe from 'stripe';

import { checkProductMatch, IProduct, PlanGroup } from '@d-id/types';

import { toast } from '@/components/toast';
import {
    Billing,
    getDataForChangePlan,
    getPrice,
    getProductPrices,
    getProducts,
    getStripeCustomerPortalUrl,
    ProductPrice,
} from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';

export function useAllProducts(enabled: boolean = true) {
    return useQuery('get-products', getProducts, {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled,
        select: data =>
            data.products.map(prod => ({
                name: prod.name,
                id: prod.id,
                metadata: { plan_group: prod.metadata.plan_group },
            })) as IProduct[],
    });
}

export function usePrice(priceId: string) {
    return useQuery(`get-price-${priceId}`, () => getPrice(priceId), {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: !!priceId,
        select: ({ price }: { price: Stripe.Price }) => {
            if (!price) {
                logger.error(`price was not found for priceId ${priceId}`);
                throw new Error('price was not found');
            }
            return price;
        },
    });
}

export function useCreditPrice(enabled: boolean) {
    const { data: products, isLoading: isLoadingProducts } = useAllProducts(enabled);
    const { user } = useAuth();
    const productId = products?.find(product =>
        user?.planGroup ? product.metadata.plan_group === user.planGroup : product.name === user?.productName
    )?.id;
    const query = useQuery('get-user-product-price', () => getProductPrices({ productId: productId!, oneTime: true }), {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: !!productId && checkProductMatch.PRO(user?.planGroup ?? user?.productName),
        select: ({ prices }: { prices: ProductPrice[] }) => {
            const oneTimePrices = prices.filter(price => price.type === 'one_time');
            return oneTimePrices?.[0];
        },
    });
    return { ...query, isLoading: query.isLoading || isLoadingProducts };
}

export function useProductBiggerPrices(priceId: string, interval: Billing) {
    const { data: price, isFetched: priceIsFetched } = usePrice(priceId);
    const productId = price?.product;
    const query = useQuery('get-user-new-product-bigger-prices', () => getProductPrices({ productId: productId as string, interval }), {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: priceIsFetched && !!productId,
        select: ({ prices }: { prices: ProductPrice[] }) => {
            const filteredPrice = prices?.filter(price => !price.metadata?.deprecated);
            return filteredPrice?.length > 0
                ? filteredPrice.sort((a, b) => parseInt(b.metadata.credits) - parseInt(a.metadata.credits))
                : undefined;
        },
    });
    return priceIsFetched && !productId ? { ...query, error: new Error('no product was found'), isFetched: true } : query;
}

export function useProductYearlyPrice(priceId: string) {
    const { data: price, isFetched: priceIsFetched } = usePrice(priceId);
    const productId = price?.product;
    const currCredits = price?.metadata?.credits;
    const query = useQuery(
        'get-user-new-product-bigger-prices',
        () => getProductPrices({ productId: productId as string, interval: 'year' as Billing }),
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            enabled: priceIsFetched && !!productId,
            select: ({ prices }: { prices: ProductPrice[] }) => {
                const filteredPrice = prices?.filter(price => !price.metadata?.deprecated);
                return filteredPrice?.length > 0
                    ? filteredPrice
                          .filter(price => price.metadata?.credits === currCredits)
                          .filter(price => !price.nickname?.includes('discounted'))[0]
                    : undefined;
            },
        }
    );
    return priceIsFetched && !productId ? { ...query, error: new Error('no product was found'), isFetched: true } : query;
}

interface dataForChangePlanResponse {
    upcomingInvoiceData: Stripe.Invoice;
    prevPrice: Stripe.Price;
    creditCardDetails?: Stripe.PaymentMethod.Card;
    currentPeriodEnd: number;
}

interface IGetDataForChangePlan {
    productName: PlanGroup;
    interval: Billing;
    priceId: string;
    coupon?: string;
    onError: () => void;
}

export function useGetDataForChangePlan({ productName, interval, priceId, coupon, onError }: IGetDataForChangePlan) {
    const { user } = useAuth();
    const isYearToYear = user?.billingInterval === 'year' && interval === 'year';
    const isYearToMonth = user?.billingInterval === 'year' && interval === 'month';
    const query = useQuery(`get-change-plan-data-${coupon ?? ''}`, () => getDataForChangePlan(priceId ?? '', coupon), {
        retry: 3,
        retryDelay: 10,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        refetchOnMount: false,
        enabled: !!priceId,
        select: ({ upcomingInvoiceData, creditCardDetails, prevPrice, currentPeriodEnd }: dataForChangePlanResponse) => {
            let { nextBillingDate, ...payments } = upcomingInvoiceData.lines.data.reduce(
                (result, line) => {
                    const key = line.proration ? 'proration' : 'nonProration';
                    const discountAmount = line.discount_amounts?.reduce((acc, discount) => acc + discount.amount, 0) ?? 0;

                    return {
                        ...result,
                        [key]: result[key] + (line.amount - discountAmount) / 100,
                        ...(key === 'nonProration' && {
                            nextBillingDate: new Date(line.period.start * 1000),
                        }),
                    };
                },
                { nextBillingDate: new Date(), proration: 0, nonProration: 0 }
            );
            const mixpanelData = {
                currentPlan: user?.planGroup,
                currentBilling: user?.billingInterval,
                requestedPlan: productName,
                requestedBilling: interval,
                prorationAmount: payments.proration,
            };
            const prevPriceCredits = prevPrice.metadata.credits;
            const newPlanData = upcomingInvoiceData.lines.data.find(line => line.price?.id === priceId) as Stripe.InvoiceLineItem;
            const newPriceCredits = newPlanData.price?.metadata.credits ?? '';
            if (isYearToYear) {
                return {
                    payments: { ...payments, nonProration: newPlanData.plan!.amount! / 100 },
                    creditCardDetails,
                    nextBillingDate: new Date(currentPeriodEnd * 1000),
                    mixpanelData,
                    newPriceId: priceId,
                    prevPriceCredits,
                    newPriceCredits,
                };
            }
            const isUpgrade = prevPrice.unit_amount! < newPlanData.price?.unit_amount!;
            if (!isUpgrade || (isUpgrade && isYearToMonth)) {
                // new plan starts at end of current plan
                nextBillingDate = new Date(currentPeriodEnd * 1000);
            }
            return {
                payments,
                creditCardDetails,
                nextBillingDate,
                mixpanelData,
                newPriceId: priceId,
                newPriceCredits,
                prevPriceCredits,
            };
        },
        onError: error => {
            if ((error as AxiosError<any>)?.response?.data?.kind === 'PlanChangeSupportNeededError') {
                toast.info({ title: `Let's talk. Please contact support to change your plan` });
            } else {
                toast.error({ toastId: 'get-change-plan-data-error', title: 'Failed to fetch plan' });
            }
            logger.error('failed to get change plan data', error);
            onError();
        },
    });

    useEffect(() => {
        if (query.data && priceId) {
            query.refetch();
        }
    }, [priceId]);

    return !priceId ? { ...query, error: new Error('no price was found'), isFetched: true } : query;
}

export function isDowngrading(currentPlan: PlanGroup, currentPrice: number, newPlan: PlanGroup, newPrice: number) {
    if (currentPlan === newPlan) {
        return currentPrice > newPrice;
    }
    return Object.values(PlanGroup).indexOf(currentPlan) > Object.values(PlanGroup).indexOf(newPlan);
}

export function useGetStripeCustomerPortalUrl() {
    return useQuery('get-stripe-customer-portal-url', getStripeCustomerPortalUrl, {
        select: response => ({
            status: response?.status,
            url: response?.data?.url,
        }),
        refetchOnWindowFocus: false,
        enabled: false,
    });
}
